/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from 'react';
import { UriOrTypeId } from 'components/Form/Common/types';
import { JRCAvatar } from 'components/JRCAvatar/JRCAvatar';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import styled, { useTheme } from 'styled-components';
import { JRCTooltip } from '../JRCTooltip/JRCTooltip';
import { ColorsOrShades } from 'styles/theme';
import { useState } from 'react';

/**
 * /!\ This component is overkilled. Use with care since it may be simplified later on!
 *
 * @see UriOrTypeId
 * @member collapsed - whether the tag is collapsed or not
 * @member label - label aside from the avatar
 * @member value - value passed as the onClick argument
 * @member onClick - onClick method
 * @member onMouseDown - callback on mouse down
 * @member variant - whether the avatar is circular or square
 * @member focusable - whether the element is focusable. Independant of onClick property
 * @member isFocused - whether the element is focused. Property used with variant for styling the component
 * @member tooltipDescription - if present, show a tooltip with the description
 * @member alt - alt attribute
 * @member fallbackUrl - url loaded if the uri leads to an undefined image
 * @member clickVariant - whether the hover/focus style is a selectable item or a deletable item
 */
export type JRCTagProps<T> = UriOrTypeId & {
    collapsed?: boolean;
    label?: string;
    value?: T;
    onClick?: (e: React.MouseEvent, value?: T) => void;
    onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
    variant?: 'circle' | 'square';
    focusable?: boolean;
    isFocused?: boolean;
    tooltipDescription?: string;
    alt?: string;
    fallbackUrl?: string;
    clickVariant?: 'select' | 'delete';
    simpleText?: boolean;
    defaultIcon?: string;
    iconOptions?: { size?: number };
    onLoad?: () => void;
};

const TagWrapper = styled.div<{ focusable: boolean; isFocused: boolean; clickVariant: 'select' | 'delete' }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    margin: 0 2px;
    min-height: 32px;
    border-radius: 4px;
    cursor: ${(props) => (props.focusable ? 'pointer' : 'default')};
    background-color: ${(props) => {
        if (props.focusable && props.isFocused) {
            return props.theme.color[config[props.clickVariant].hoverBGColor];
        } else {
            return props.theme.color[config[props.clickVariant].backgroundColor];
        }
    }};
    color: ${(props) => {
        if (props.focusable && props.isFocused) {
            return props.theme.color[config[props.clickVariant].hoverColor];
        } else {
            return props.theme.color[config[props.clickVariant].color];
        }
    }};

    i.delete {
        position: absolute;
        margin: 0;
        padding: 0;
        left: 8px;
        width: 25px;
        height: 25px;
        line-height: 24px;
        text-align: center;
        border-radius: 12px;
        background-color: #daa9;
        display: none;
    }

    :hover {
        ${(props) =>
            props.focusable && `background-color: ${props.theme.color[config[props.clickVariant].hoverBGColor]}`};
        ${(props) => props.focusable && `color: ${props.theme.color[config[props.clickVariant].hoverColor]}`};
    }

    :hover i.delete {
        ${(props) => props.focusable && props.clickVariant === 'delete' && `display: block`};
    }

    & .delete i.delete {
        ${(props) => props.focusable && props.isFocused && props.clickVariant === 'delete' && `display: block`};
    }
`;

const AvatarWrapper = styled.div`
    background-color: ${(props) => props.theme.color.lavender};
    border-radius: 100%;
    overflow: hidden;
`;

const TagLabel = styled.div`
    margin-left: 4px;
`;

const IconWrapper = styled.div`
    margin-left: -3px;
`;

type ConfigType = {
    color: ColorsOrShades;
    backgroundColor: ColorsOrShades;
    hoverColor: ColorsOrShades;
    hoverBGColor: ColorsOrShades;
};

const config: {
    select: ConfigType;
    delete: ConfigType;
} = {
    select: {
        color: 'grey4',
        backgroundColor: 'white',
        hoverColor: 'grey5',
        hoverBGColor: 'grey0',
    },
    delete: {
        color: 'grey5',
        backgroundColor: 'grey0',
        hoverColor: 'grey4',
        hoverBGColor: 'orangeL80',
    },
};

/*
const uri = 'uri' in props ? props.uri : `${props.type}/${props.recordId}`;
    if (props.uri !== undefined && (props.uri.indexOf('folder') !== -1 || props.uri.indexOf('rootFolder') !== -1)) {
        return (
            <>
                <JRCFolder color="#ccc" size={'sm'}></JRCFolder>
                {!props.collapsed && props.label && <TagLabel>{props.label}</TagLabel>}
                <JRCIcon name="icon-times" size={16} color="orange" />
            </>
        );
    }
    */

export function JRCTag<T>(props: JRCTagProps<T>) {
    const uri =
        'uri' in props ? props.uri : props.type && props.recordId ? `${props.type}/${props.recordId}` : undefined;
    const clickVariant = props.clickVariant || 'delete';
    const [isAvatarOnError, setAvatarOnError] = useState(false);
    const theme = useTheme();
    const getAvatar = () =>
        React.createElement(
            TagWrapper,
            {
                onClick: props.onClick
                    ? (e: React.MouseEvent<Element, MouseEvent>) => {
                          if (props.onClick) props.onClick(e, props.value);
                      }
                    : undefined,
                focusable: props.focusable,
                isFocused: props.isFocused,
                clickVariant,
            },
            <>
                {!props.simpleText && !isAvatarOnError && (
                    <AvatarWrapper>
                        <JRCAvatar
                            alt={props.alt}
                            uri={uri}
                            url={!uri ? props.fallbackUrl : undefined}
                            variant={props.variant}
                            focusable={props.focusable}
                            fallbackUrl={props.fallbackUrl}
                            onError={() => setAvatarOnError(true)}
                        />
                    </AvatarWrapper>
                )}
                {isAvatarOnError && props.defaultIcon && (
                    <IconWrapper>
                        <JRCIcon name={props.defaultIcon} color={theme.color.primary} size={25} variant="circle" />
                    </IconWrapper>
                )}
                {!props.collapsed && props.label && <TagLabel>{props.label}</TagLabel>}
                {!props.simpleText && (!isAvatarOnError || props.defaultIcon) && (
                    <JRCIcon className={'delete'} name="icon-times" size={16} color="orange" />
                )}
            </>,
        );

    return props.tooltipDescription && props.collapsed ? (
        <JRCTooltip description={props.tooltipDescription}>{getAvatar()}</JRCTooltip>
    ) : (
        <> {getAvatar()} </>
    );
}
